import { create } from 'zustand';

export type Theme = 'secure-agility' | 'secure-agility-dark';

export interface AppSettings {
  theme: Theme;
}

const STORAGE_KEY = 'clara-app-settings';
const DEFAULT_SETTINGS: AppSettings = {
  theme: 'secure-agility-dark',
};

function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme;
}

function getInitialSettings(): AppSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<AppSettings>;
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch {
    // Invalid JSON or parse error, fall back to defaults
  }
  return DEFAULT_SETTINGS;
}

interface AppState {
  settings: AppSettings;
  setTheme: (theme: Theme) => void;
  updateSettings: (updates: Partial<AppSettings>) => void;
}

export const useAppSettingsStore = create<AppState>((set) => ({
  settings: getInitialSettings(),
  setTheme: (theme) => {
    set((state) => ({
      settings: { ...state.settings, theme },
    }));
    const commit = () => {
      const newSettings = { ...useAppSettingsStore.getState().settings, theme };
      applyTheme(theme);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
    };

    if (document.startViewTransition) {
      document.startViewTransition(commit);
    } else {
      commit();
    }
  },
  updateSettings: (updates) => {
    set((state) => ({
      settings: { ...state.settings, ...updates },
    }));
    const commit = () => {
      const newSettings = useAppSettingsStore.getState().settings;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
      if (updates.theme) {
        applyTheme(updates.theme);
      }
    };

    if (document.startViewTransition) {
      document.startViewTransition(commit);
    } else {
      commit();
    }
  },
}));

applyTheme(useAppSettingsStore.getState().settings.theme);
